import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  CircularProgress, Chip, Stack, Alert, MenuItem, Stepper, Step, StepLabel,
  Paper, Divider
} from '@mui/material';
import { School, CheckCircle, HowToReg, Send } from '@mui/icons-material';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
const SCHOOL_TYPES = ['primary','secondary','combined','technical','special'];
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());

export default function SchoolRegistration() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    axios.get((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/packages')
      .then(r => { if (r.data.success) setPackages(r.data.data); })
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    school_name:'', school_type:'secondary', school_code:'',
    address:'', city:'', region:'', country:'Eswatini',
    phone:'', email:'', website:'',
    admin_name:'', admin_email:'', admin_phone:'',
    student_count:'', teacher_count:'', motivation:'', selected_package:'starter'
  });
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async () => {
    setLoading(true); setError('');
    try {
      await api.post('/platform/register', form);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.message || 'Submission failed. Please try again.'); }
    finally { setLoading(false); }
  };

  if (submitted) return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f7fa', display:'flex', alignItems:'center', justifyContent:'center', p:2 }}>
      <Card variant="outlined" sx={{ maxWidth:520, width:'100%', borderRadius:3, textAlign:'center', p:3 }}>
        <CheckCircle sx={{ fontSize:72, color:'#2e7d32', mb:2 }} />
        <Typography variant="h5" fontWeight={800} gutterBottom>Registration Submitted!</Typography>
        <Typography color="text.secondary" sx={{ mb:2 }}>
          Thank you for registering <strong>{form.school_name}</strong>. Our team will review your application and contact <strong>{form.admin_email}</strong> within 1–2 business days.
        </Typography>
        <Alert severity="info" sx={{ textAlign:'left', fontSize:12 }}>
          Once approved, you will receive your school's unique login URL and administrator credentials by email.
        </Alert>
      </Card>
    </Box>
  );

  const steps = ['School Details', 'Administrator', 'Choose Package', 'Review & Submit'];

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f7fa', py:4, px:2 }}>
      {/* Header */}
      <Box sx={{ textAlign:'center', mb:4 }}>
        <Stack direction="row" justifyContent="center" spacing={1.5} alignItems="center" sx={{ mb:1 }}>
          <School sx={{ fontSize:40, color:'#0f2b4a' }} />
          <Box>
            <Typography variant="h4" fontWeight={800} color="#0f2b4a">School Management Platform</Typography>
            <Typography color="text.secondary">Register your school to get started</Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ maxWidth:680, mx:'auto' }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb:4 }}>
          {steps.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

        <Card variant="outlined" sx={{ borderRadius:3 }}>
          <CardContent sx={{ p:3 }}>

            {/* Step 0: School details */}
            {step === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography fontWeight={700} gutterBottom>School Information</Typography></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="School Name *" value={form.school_name} onChange={setF('school_name')} /></Grid>
                <Grid item xs={6} sm={4}>
                  <TextField select fullWidth size="small" label="School Type" value={form.school_type} onChange={setF('school_type')}>
                    {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Ministry School Code" value={form.school_code} onChange={setF('school_code')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Country" value={form.country} onChange={setF('country')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Region / District" value={form.region} onChange={setF('region')} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="City / Town" value={form.city} onChange={setF('city')} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Physical Address" value={form.address} onChange={setF('address')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={form.phone} onChange={setF('phone')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="School Email" type="email" value={form.email} onChange={setF('email')} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Website (optional)" value={form.website} onChange={setF('website')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Approx. Student Count" value={form.student_count} onChange={setF('student_count')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Approx. Teacher Count" value={form.teacher_count} onChange={setF('teacher_count')} /></Grid>
              </Grid>
            )}

            {/* Step 1: Admin details */}
            {step === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography fontWeight={700} gutterBottom>School Administrator</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                    This person will become the first admin user and can then create all other staff accounts.
                  </Typography>
                </Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Full Name *" value={form.admin_name} onChange={setF('admin_name')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Email Address *" type="email" value={form.admin_email} onChange={setF('admin_email')} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={form.admin_phone} onChange={setF('admin_phone')} /></Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Why are you interested in this platform?" value={form.motivation} onChange={setF('motivation')} multiline rows={4}
                    placeholder="Tell us about your school and what you hope to achieve with this system…" />
                </Grid>
              </Grid>
            )}


            {/* Step 2: Package selection */}
            {step === 2 && (
              <Box>
                <Typography fontWeight={700} gutterBottom>Choose Your Package</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select the plan that best suits your school. You can upgrade at any time.
                </Typography>
                <Grid container spacing={2}>
                  {packages.map(pkg => (
                    <Grid item xs={12} sm={6} key={pkg.code}>
                      <Box
                        onClick={() => setForm(f => ({ ...f, selected_package: pkg.code }))}
                        sx={{
                          border: `2px solid ${form.selected_package === pkg.code ? pkg.color : '#e0e0e0'}`,
                          borderRadius: 2, p: 2.5, cursor: 'pointer',
                          background: form.selected_package === pkg.code ? `${pkg.color}08` : 'white',
                          transition: 'all 0.2s',
                          position: 'relative',
                          '&:hover': { borderColor: pkg.color, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }
                        }}
                      >
                        {pkg.is_popular ? (
                          <Box sx={{ position:'absolute', top:-10, right:12, bgcolor: pkg.color, color:'white',
                            fontSize:10, fontWeight:700, px:1.5, py:0.3, borderRadius:10 }}>
                            MOST POPULAR
                          </Box>
                        ) : null}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Box>
                            <Typography fontSize={22}>{pkg.icon}</Typography>
                            <Typography fontWeight={800} fontSize={16} sx={{ color: pkg.color }}>{pkg.name}</Typography>
                            <Typography fontSize={11} color="text.secondary">{pkg.tagline}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={800} fontSize={18} sx={{ color: pkg.color }}>
                              E{parseFloat(pkg.price_annual).toLocaleString()}
                            </Typography>
                            <Typography fontSize={10} color="text.secondary">per year</Typography>
                            {pkg.price_monthly && (
                              <Typography fontSize={10} color="text.secondary">
                                E{parseFloat(pkg.price_monthly).toLocaleString()}/month
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          {(pkg.features || []).slice(0, 5).map((f, i) => (
                            <Typography key={i} fontSize={11} sx={{ py: 0.2 }}>
                              ✓ {f}
                            </Typography>
                          ))}
                          {(pkg.features || []).length > 5 && (
                            <Typography fontSize={11} color="text.secondary">
                              + {pkg.features.length - 5} more features
                            </Typography>
                          )}
                        </Box>
                        {form.selected_package === pkg.code && (
                          <Box sx={{ mt: 1, textAlign: 'center', color: pkg.color, fontWeight: 700, fontSize: 12 }}>
                            ✓ Selected
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {/* Step 3: Review */}
            {step === 3 && (
              <Box>
                <Typography fontWeight={700} gutterBottom>Review Your Application</Typography>
                <Grid container spacing={2}>
                  {[
                    ['School Name', form.school_name],
                    ['Type', ptLabel(form.school_type)],
                    ['Region', `${form.city || ''}${form.region ? `, ${form.region}` : ''}, ${form.country}`],
                    ['Phone', form.phone || '—'],
                    ['Email', form.email || '—'],
                    ['Students', form.student_count || '—'],
                    ['Administrator', form.admin_name],
                    ['Admin Email', form.admin_email],
                    ['Package', packages.find(p => p.code === form.selected_package)?.name || form.selected_package],
                    ['Price', `E${parseFloat(packages.find(p => p.code === form.selected_package)?.price_annual || 0).toLocaleString()}/year`],
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Typography fontSize={11} color="text.secondary">{label}</Typography>
                      <Typography fontSize={13} fontWeight={600}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my:2 }} />
                <Alert severity="info" sx={{ fontSize:12 }}>
                  By submitting, you agree that a platform administrator will review your application. You will receive your login URL and credentials by email once approved.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between" sx={{ mt:2 }}>
          <Button onClick={back} disabled={step === 0}>Back</Button>
          {step < 3 ? (
            <Button variant="contained" onClick={next}
              disabled={(step === 0 && !form.school_name) || (step === 1 && (!form.admin_name || !form.admin_email))}
              sx={{ bgcolor:'#0f2b4a' }}>Continue</Button>
          ) : (
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={submit} disabled={loading} sx={{ bgcolor:'#2e7d32' }}>
              Submit Registration
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
